import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faPlus, faLayerGroup, faSearch } from '@fortawesome/free-solid-svg-icons';

import { CircularProgress, Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow, Tooltip } from "@mui/material";

import { useNavigate, useParams } from 'react-router-dom';

import { useState, useEffect, useMemo } from "react";

import { Report } from '../../reports/vouchers_report/Vouchers_report';
import TablePaginationActions from '../../../../base-components/TablePaginationActions';
import CustomModal from '../../../../base-components/CustomModal';
import Popup from '../../../../base-components/Popup';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  PageHeader,
  StatCard,
  EmptyState,
  TableShell,
  StatusChip,
} from "../../../../ui";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { motion, useReducedMotion } from "motion/react";
import {
  isVoucherSettled,
  voucherPendingAmount,
  voucherPaymentDateValue,
  voucherStatusMeta,
  voucherTotalAmount,
} from "../../../../utils/voucherPayment";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const controlClass =
  "border border-gx-border bg-gx-surface rounded-gx-control py-2.5 px-3 outline-0 w-full text-sm text-gx-ink min-h-[44px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gx-red";

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return `${value ?? 0} Rs`;
  return `Rs ${n.toLocaleString("en-US")}`;
}

function formatMaybeDate(value) {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMM DD, YYYY") : "—";
}

function voucherStatus(voucher) {
  return voucherStatusMeta(voucher);
}

function voucherTotal(voucher) {
  return voucherTotalAmount(voucher);
}

function voucherPending(voucher) {
  return voucherPendingAmount(voucher);
}

function voucherDueDate(voucher) {
  return voucher?.invoice?.due_date || voucher?.receipt?.due_date;
}

function voucherPaymentDate(voucher) {
  return voucherPaymentDateValue(voucher);
}

function getMemberMongoIdFromVouchers(list) {
  if (!Array.isArray(list)) return null;
  for (const v of list) {
    const m = v?.member_id;
    if (!m) continue;
    if (typeof m === 'object' && m._id) return String(m._id);
    if (typeof m === 'string') return m;
  }
  return null;
}

function staffCanCreateVouchers(role) {
  return ['superadmin', 'admin', 'clubadmin'].includes((role || '').toLowerCase());
}
function VouchersTable() {
  const navigate = useNavigate()
  const { id } = useParams()
  const reduceMotion = useReducedMotion()
  const [loading, setLoading] = useState(true)
  const [vouchers, setVouchers] = useState([])
  const [role, setRole] = useState('')
  const [location_id, setLocationID] = useState('')
  const [company_id, setCompanyID] = useState('')
  const [member_id, setMemberID] = useState('')
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0)
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("");

  const [VoucherType, setSubscriptionType] = useState("");
  const [paymentFromDate, setPaymentFromDate] = useState("");
  const [paymentToDate, setPaymentToDate] = useState("");
  const [dueFromDate, setDueFromDate] = useState("");
  const [dueToDate, setDueToDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [layout, setLayout] = useState("cards");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [allowVoucherCreation, setAllowVoucherCreation] = useState(false);
  const [allowVoucherInstallments, setAllowVoucherInstallments] = useState(false);
  const [isSplitModalOpen, setIsSplitModalOpen] = useState(false);
  const [splitTargetVoucher, setSplitTargetVoucher] = useState(null);
  const [splitNumberOfInstallments, setSplitNumberOfInstallments] = useState(2);
  const [splitButtonLoading, setSplitButtonLoading] = useState(false);
  const [splitInstallmentDueDates, setSplitInstallmentDueDates] = useState([]);
  const [splitDateError, setSplitDateError] = useState('');
  const [splitAmountMode, setSplitAmountMode] = useState('equal');
  const [splitCustomAmounts, setSplitCustomAmounts] = useState([]);
  const [splitAmountError, setSplitAmountError] = useState('');
  const [splitSubscriptionResolved, setSplitSubscriptionResolved] = useState(null);
  const [popup, setPopup] = useState({ open: false, type: 'success', message: '' });



  useEffect(() => {
    const sessionStr = sessionStorage.getItem('user-info');
    if (sessionStr) {
      const session = JSON.parse(sessionStr);
      setRole(session.user.role)
      setCompanyID(session.user.company_id)
      setLocationID(session.user.location_id)
      setMemberID(session.user.member_id)
    }
    else {
      navigate('/login');
    }
  }, [])

  useEffect(() => {
    const fetchVouchers = async () => {
      if (!role) return;
      setLoading(true);
      await getVouchers();
      setLoading(false);
    };
    fetchVouchers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // Fetch bank accounts when component mounts
  useEffect(() => {
    if (company_id) {
      getBankAccounts();
    }
  }, [company_id]);

  useEffect(() => {
    if (!company_id) return;
    fetch(`${BASE_URL}company/${company_id}`)
      .then((r) => r.json())
      .then((data) => {
        setAllowVoucherCreation(!!data.allow_voucher_creation);
        setAllowVoucherInstallments(!!data.allow_voucher_installments);
      })
      .catch(() => {
        setAllowVoucherCreation(false);
        setAllowVoucherInstallments(false);
      });
  }, [company_id]);

  const getBankAccounts = async () => {
    try {
      const response = await fetch(`${BASE_URL}company-bank-accounts/company/${company_id}`);
      const data = await response.json();
      if (response.ok) {
        setBankAccounts(data.bankAccounts || []);
        console.log('Bank accounts fetched:', data.bankAccounts);
      } else {
        console.log('No bank accounts found or error:', data.message);
        setBankAccounts([]);
      }
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
      setBankAccounts([]);
    }
  };

  const getVouchers = async () => {
    await fetch(BASE_URL + `voucher/getVouchersBySubscriptionID/${id}`)
      .then((response) => response.json())
      .then((data) => {
        setVouchers(data.vouchers || [])
      })
      .catch((error) => {
        console.log(error)
      });
  }
  console.log("id", vouchers)
  const scopedVouchers = useMemo(() => {
    let filtered = vouchers || [];
    const q = (searchQuery || "").trim().toLowerCase();
    if (q) {
      filtered = filtered.filter((voch) =>
        String(voch.invoice_no || "").toLowerCase().includes(q) ||
        String(voch.voucher_type || "").toLowerCase().includes(q) ||
        String(voch.member_id?.name || "").toLowerCase().includes(q)
      );
    }
    if (fromDate && toDate) {
      const startFilterDate = new Date(fromDate);
      startFilterDate.setHours(0, 0, 0, 0);
      const endFilterDate = new Date(toDate);
      endFilterDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter((voucher) => {
        if (!voucher.voucher_date) return false;
        const voucherDate = new Date(voucher.voucher_date);
        return voucherDate >= startFilterDate && voucherDate <= endFilterDate;
      });
    }
    if (paymentFromDate && paymentToDate) {
      const startPaymentDate = new Date(paymentFromDate);
      startPaymentDate.setHours(0, 0, 0, 0);
      const endPaymentDate = new Date(paymentToDate);
      endPaymentDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter((voucher) => {
        if (voucher.receipt) {
          const paymentDate = voucher.voucher_date ? new Date(voucher.voucher_date) : null;
          return paymentDate && paymentDate >= startPaymentDate && paymentDate <= endPaymentDate;
        }
        const paymentDate = voucher?.invoice?.payment_date ? new Date(voucher.invoice.payment_date) : null;
        return paymentDate && paymentDate >= startPaymentDate && paymentDate <= endPaymentDate;
      });
    }
    if (dueFromDate && dueToDate) {
      const startDueDate = new Date(dueFromDate);
      startDueDate.setHours(0, 0, 0, 0);
      const endDueDate = new Date(dueToDate);
      endDueDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter((voucher) => {
        const invoiceDueDate = voucher?.invoice?.due_date ? new Date(voucher.invoice.due_date) : null;
        const receiptDueDate = voucher?.receipt?.due_date ? new Date(voucher.receipt.due_date) : null;
        return (
          (invoiceDueDate && invoiceDueDate >= startDueDate && invoiceDueDate <= endDueDate) ||
          (receiptDueDate && receiptDueDate >= startDueDate && receiptDueDate <= endDueDate)
        );
      });
    }
    if (VoucherType) {
      filtered = filtered.filter(
        (voucher) => voucher.voucher_type?.toLowerCase() === VoucherType.toLowerCase()
      );
    }
    return filtered;
  }, [
    vouchers,
    searchQuery,
    fromDate,
    toDate,
    paymentFromDate,
    paymentToDate,
    dueFromDate,
    dueToDate,
    VoucherType,
  ]);

  const filtered_vouchers = useMemo(() => {
    if (!status) return scopedVouchers;
    return scopedVouchers.filter((voucher) => {
      const meta = voucherStatus(voucher);
      if (status.toLowerCase() === "paid") return meta.key === "paid";
      if (status.toLowerCase() === "pending") return meta.key === "pending";
      if (status.toLowerCase() === "overdue") return meta.key === "overdue";
      return meta.label.toLowerCase() === status.toLowerCase();
    });
  }, [scopedVouchers, status]);

  const kpis = useMemo(() => {
    const list = scopedVouchers || [];
    let paid = 0;
    let pending = 0;
    let overdue = 0;
    let outstanding = 0;
    list.forEach((voucher) => {
      const meta = voucherStatus(voucher);
      if (meta.key === "paid") paid += 1;
      else if (meta.key === "overdue") overdue += 1;
      else pending += 1;
      outstanding += voucherPending(voucher);
    });
    return { total: list.length, paid, pending, overdue, outstanding };
  }, [scopedVouchers]);

  const paginatedVouchers = !filtered_vouchers || filtered_vouchers.length === 0
    ? []
    : rowsPerPage === -1
      ? filtered_vouchers
      : filtered_vouchers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleNavigate = () => {
    navigate("/ledgers");
  }

  const handleAddVoucher = async () => {
    let memberMongoId = getMemberMongoIdFromVouchers(vouchers);
    if (!memberMongoId && id) {
      try {
        const res = await fetch(`${BASE_URL}subscription/get-subscription/${id}`);
        const data = await res.json();
        if (res.ok && data.subscription?.member_id) {
          memberMongoId = String(data.subscription.member_id);
        }
      } catch (_) {
        /* ignore */
      }
    }
    navigate('/admin/vouchers/create', {
      state: {
        preselectedMemberId: memberMongoId || undefined,
        returnSubscriptionId: id,
      },
    });
  };
  const handleDownload = () => {
    Report(filtered_vouchers)
  }

  const computeInstallmentShares = (total, n) => {
    const safeTotal = Number(total) || 0;
    const safeN = Math.max(2, Math.floor(Number(n) || 0));
    const base = Math.floor(safeTotal / safeN);
    const last = safeTotal - base * (safeN - 1);
    const shares = [];
    for (let i = 0; i < safeN; i++) {
      shares.push(i === safeN - 1 ? last : base);
    }
    return shares;
  };

  const validateInstallmentAmounts = (amountStrings, targetTotal, n) => {
    if (!Array.isArray(amountStrings) || amountStrings.length !== n) {
      return `Enter an amount for each of the ${n} installments.`;
    }
    let sum = 0;
    for (let i = 0; i < amountStrings.length; i++) {
      const val = Number(amountStrings[i]);
      if (!Number.isFinite(val) || val <= 0) {
        return `Installment #${i + 1} amount must be greater than zero.`;
      }
      sum += val;
    }
    const target = Number(targetTotal) || 0;
    if (Math.abs(sum - target) > 0.009) {
      return `Installment amounts must sum to ${target} (outstanding pending); current sum is ${sum}.`;
    }
    return '';
  };

  const getSplitDisplayAmounts = () => {
    const n = Math.max(2, Math.floor(Number(splitNumberOfInstallments) || 0));
    const principal = splitPreviewPrincipal(splitTargetVoucher);
    if (splitAmountMode === 'custom') {
      return Array.from({ length: n }, (_, i) => {
        const raw = splitCustomAmounts[i];
        const num = Number(raw);
        return Number.isFinite(num) ? num : 0;
      });
    }
    return computeInstallmentShares(principal, n);
  };

  const canSplitVoucher = (voucher) => {
    if (!voucher) return false;
    const installmentsOn = !!(voucher.allow_voucher_installments ?? allowVoucherInstallments);
    if (!installmentsOn) return false;
    if (!staffCanCreateVouchers(role)) return false;
    if (voucher.voucher_type !== 'Invoice') return false;
    if (voucher.is_active !== true) return false;
    if (voucher.is_split === true) return false;
    if (voucher.parent_voucher_id) return false;
    if ((voucher.paid_amount || 0) > 0) return false;
    if (!voucher.invoice) return false;
    if (isVoucherSettled(voucher)) return false;
    if (!voucher.invoice.due_date) return false;
    const pending = Number(voucher.invoice.pending_amount ?? 0);
    const total = Number(voucher.invoice.total_amount ?? 0);
    if (pending <= 0 || total <= 0) return false;
    return true;
  };

  const splitPreviewPrincipal = (voucher) =>
    Number(voucher?.invoice?.pending_amount ?? voucher?.invoice?.total_amount ?? 0);

  const normalizeSplitDate = (d) => {
    if (d == null) return null;
    const jd = dayjs(d);
    return jd.isValid() ? jd.startOf('day') : null;
  };

  const getSplitSubscriptionPeriod = () => {
    const v = splitTargetVoucher;
    if (!v) return { start: null, end: null };
    const embedded = v.subscription_id;
    if (
      embedded &&
      typeof embedded === 'object' &&
      embedded.start_date &&
      embedded.end_date
    ) {
      return {
        start: dayjs(embedded.start_date).startOf('day'),
        end: dayjs(embedded.end_date).startOf('day'),
      };
    }
    const res = splitSubscriptionResolved;
    if (res?.start_date && res?.end_date) {
      return {
        start: dayjs(res.start_date).startOf('day'),
        end: dayjs(res.end_date).startOf('day'),
      };
    }
    return { start: null, end: null };
  };

  const computeAutoSplitDueDates = (start, end, n) => {
    const safeN = Math.max(2, Math.floor(Number(n) || 0));
    if (!start || !end) return Array(safeN).fill(null);
    const startMs = start.valueOf();
    const endMs = end.valueOf();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
      return Array(safeN).fill(null);
    }
    const stepMs = (endMs - startMs) / safeN;
    const dates = [];
    for (let i = 0; i < safeN; i++) {
      if (i === safeN - 1) {
        dates.push(dayjs(endMs));
      } else {
        dates.push(dayjs(startMs + Math.floor(stepMs * (i + 1))));
      }
    }
    return dates;
  };

  const validateSplitDueDates = (dates, subEnd) => {
    if (!Array.isArray(dates) || dates.length < 2) return 'At least 2 installments are required.';
    const todayKey = dayjs().startOf('day').valueOf();
    const seen = new Set();
    for (let i = 0; i < dates.length; i++) {
      const jd = normalizeSplitDate(dates[i]);
      if (!jd) return `Installment #${i + 1} has an invalid date.`;
      const k = jd.valueOf();
      if (i === 0 && k < todayKey) return 'First installment due date cannot be in the past.';
      if (seen.has(k)) return 'Installment due dates must be unique (no duplicate days).';
      seen.add(k);
      if (i > 0) {
        const prevJd = normalizeSplitDate(dates[i - 1]);
        if (!prevJd) return `Installment #${i} has an invalid date.`;
        if (k <= prevJd.valueOf()) {
          return 'Installment due dates must be in strictly ascending order.';
        }
      }
      if (subEnd && k > subEnd.startOf('day').valueOf()) {
        return `Installment due date #${i + 1} cannot be after the membership end date.`;
      }
    }
    return '';
  };

  // Load subscription dates when the row only has subscription_id (not populated).
  useEffect(() => {
    if (!isSplitModalOpen || !splitTargetVoucher) return;
    const sub = splitTargetVoucher.subscription_id;
    const sid =
      sub && typeof sub === 'object' && sub._id
        ? String(sub._id)
        : sub
          ? String(sub)
          : null;
    if (!sid) return;
    const embeddedHasPeriod =
      sub &&
      typeof sub === 'object' &&
      sub.start_date &&
      sub.end_date;
    if (embeddedHasPeriod) {
      setSplitSubscriptionResolved(null);
      return;
    }
    let cancelled = false;
    fetch(`${BASE_URL}subscription/get-subscription/${sid}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled || !data?.subscription) return;
        setSplitSubscriptionResolved(data.subscription);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isSplitModalOpen, splitTargetVoucher?._id, splitTargetVoucher?.subscription_id]);

  useEffect(() => {
    if (!isSplitModalOpen || !splitTargetVoucher) return;
    const n = Math.max(2, Math.floor(Number(splitNumberOfInstallments) || 0));
    const { start, end } = getSplitSubscriptionPeriod();
    if (!start || !end) {
      setSplitInstallmentDueDates(Array(n).fill(null));
      setSplitDateError(
        'Subscription start/end date is missing; cannot auto-fill due dates. Loading…'
      );
      return;
    }
    const today = dayjs().startOf('day');
    if (end.isBefore(today, 'day')) {
      setSplitInstallmentDueDates(Array(n).fill(null));
      setSplitDateError(
        'Membership end date has already passed; installments cannot be created.'
      );
      return;
    }
    const spreadStart = start.isBefore(today) ? today : start;
    const next = computeAutoSplitDueDates(spreadStart, end, n);
    setSplitInstallmentDueDates(next);
    setSplitDateError(validateSplitDueDates(next, end));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSplitModalOpen,
    splitNumberOfInstallments,
    splitSubscriptionResolved,
    splitTargetVoucher?._id,
    splitTargetVoucher?.subscription_id?.start_date,
    splitTargetVoucher?.subscription_id?.end_date,
    splitTargetVoucher?.subscription_id?._id,
  ]);

  useEffect(() => {
    if (!isSplitModalOpen || splitAmountMode !== 'custom' || !splitTargetVoucher) return;
    const n = Math.max(2, Math.floor(Number(splitNumberOfInstallments) || 0));
    const equal = computeInstallmentShares(splitPreviewPrincipal(splitTargetVoucher), n);
    setSplitCustomAmounts(equal.map(String));
    setSplitAmountError(validateInstallmentAmounts(equal.map(String), splitPreviewPrincipal(splitTargetVoucher), n));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSplitModalOpen, splitAmountMode, splitNumberOfInstallments, splitTargetVoucher?._id]);

  const openSplitModal = (voucher) => {
    setSplitTargetVoucher(voucher);
    setSplitNumberOfInstallments(2);
    setSplitInstallmentDueDates([]);
    setSplitDateError('');
    setSplitAmountMode('equal');
    setSplitCustomAmounts([]);
    setSplitAmountError('');
    setSplitSubscriptionResolved(null);
    setIsSplitModalOpen(true);
  };

  const handleSplitSubmit = async () => {
    if (!splitTargetVoucher) return;
    const n = Math.floor(Number(splitNumberOfInstallments) || 0);
    if (!Number.isInteger(n) || n < 2) {
      setPopup({ open: true, type: 'failure', message: 'Number of installments must be an integer of 2 or more.' });
      setTimeout(() => setPopup((p) => ({ ...p, open: false })), 3000);
      return;
    }
    const { end } = getSplitSubscriptionPeriod();
    const dateErr = validateSplitDueDates(splitInstallmentDueDates, end);
    if (dateErr) {
      setSplitDateError(dateErr);
      setPopup({ open: true, type: 'failure', message: dateErr });
      setTimeout(() => setPopup((p) => ({ ...p, open: false })), 3000);
      return;
    }
    const dueDatesIso = splitInstallmentDueDates.map((d) => {
      const jd = normalizeSplitDate(d);
      return jd ? jd.toISOString() : null;
    });
    if (dueDatesIso.length !== n || dueDatesIso.some((x) => !x)) {
      setPopup({ open: true, type: 'failure', message: 'Every installment must have a valid due date.' });
      setTimeout(() => setPopup((p) => ({ ...p, open: false })), 3000);
      return;
    }
    const principal = splitPreviewPrincipal(splitTargetVoucher);
    let payloadAmounts;
    if (splitAmountMode === 'custom') {
      const amountErr = validateInstallmentAmounts(splitCustomAmounts, principal, n);
      if (amountErr) {
        setSplitAmountError(amountErr);
        setPopup({ open: true, type: 'failure', message: amountErr });
        setTimeout(() => setPopup((p) => ({ ...p, open: false })), 3000);
        return;
      }
      payloadAmounts = splitCustomAmounts.map((a) => Number(a));
    }
    setSplitButtonLoading(true);
    try {
      const response = await fetch(`${BASE_URL}voucher/split/${splitTargetVoucher._id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          numberOfInstallments: n,
          dueDates: dueDatesIso,
          ...(payloadAmounts ? { amounts: payloadAmounts } : {}),
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setPopup({ open: true, type: 'failure', message: data.message || 'Failed to split voucher.' });
      } else {
        setPopup({ open: true, type: 'success', message: data.message || 'Voucher split successfully.' });
        setIsSplitModalOpen(false);
        setSplitSubscriptionResolved(null);
        await getVouchers();
      }
      setTimeout(() => setPopup((p) => ({ ...p, open: false })), 3000);
    } catch (error) {
      console.error('Error splitting voucher:', error);
      setPopup({ open: true, type: 'failure', message: 'Failed to split voucher.' });
      setTimeout(() => setPopup((p) => ({ ...p, open: false })), 3000);
    } finally {
      setSplitButtonLoading(false);
    }
  };

  const handleSearch = (search) => {
    setSearchQuery(search);
    setPage(0);
  };

  const handleFilter = () => {
    setPage(0);
  };

  const handleClear = () => {
    setSearchQuery("");
    setFromDate("");
    setToDate("");
    setStatus("");
    setSubscriptionType("");
    setDueFromDate("");
    setDueToDate("");
    setPaymentFromDate("");
    setPaymentToDate("");
    setPage(0);
  };

  const selectStatus = (key) => {
    const next = key === "paid" ? "Paid" : key === "pending" ? "Pending" : key === "overdue" ? "overdue" : "";
    setStatus((prev) => (prev === next ? "" : next));
    setPage(0);
  };

  const filtersActive = Boolean(
    searchQuery || fromDate || toDate || status || VoucherType || paymentFromDate || paymentToDate || dueFromDate || dueToDate
  );
  const memberName = vouchers[0]?.member_id?.name;

  const stagger = (index) =>
    reduceMotion
      ? false
      : {
          initial: { opacity: 0, y: 12 },
          animate: { opacity: 1, y: 0 },
          transition: {
            duration: 0.45,
            delay: index * 0.06,
            ease: [0.16, 1, 0.3, 1],
          },
        };

  const kpiCards = [
    {
      key: "paid",
      label: "Paid",
      value: kpis.paid.toLocaleString("en-US"),
      icon: <CheckCircleOutlineOutlinedIcon fontSize="small" />,
      accent: "#047857",
      hint: "Settled vouchers",
      featured: status === "Paid",
    },
    {
      key: "pending",
      label: "Pending",
      value: kpis.pending.toLocaleString("en-US"),
      icon: <HourglassEmptyIcon fontSize="small" />,
      accent: "#F7931E",
      hint: "Awaiting payment",
      featured: status === "Pending",
    },
    {
      key: "overdue",
      label: "Overdue",
      value: kpis.overdue.toLocaleString("en-US"),
      icon: <EventBusyIcon fontSize="small" />,
      accent: "#D71920",
      hint: "Need collection",
      featured: status === "overdue",
    },
    {
      key: "outstanding",
      label: "Outstanding",
      value: formatMoney(kpis.outstanding),
      icon: <AccountBalanceWalletIcon fontSize="small" />,
      accent: "#D71920",
      hint: "Open invoice balance",
      featured: false,
    },
  ];

  const openVoucher = (voucher) => {
    if (voucher?.voucher_type === "Receipt") navigate(`/recipt/${voucher?._id}`);
    else navigate(`/voucher/${voucher?._id}`);
  };

  const renderActions = (voucher) => (
    <div className="flex items-center justify-end gap-1 shrink-0">
      <Tooltip title="View voucher">
        <button
          type="button"
          aria-label={`View voucher ${voucher?.invoice_no || ""}`}
          className="gx-cta-secondary min-h-[44px] min-w-[44px] px-3 gap-1"
          onClick={() => openVoucher(voucher)}
        >
          <VisibilityOutlinedIcon fontSize="small" />
          <span className="hidden sm:inline">View</span>
        </button>
      </Tooltip>
      {canSplitVoucher(voucher) ? (
        <Tooltip title="Split this voucher into installments">
          <button
            type="button"
            aria-label={`Split voucher ${voucher?.invoice_no || ""}`}
            className="gx-cta-secondary min-h-[44px] px-3 gap-1"
            onClick={() => openSplitModal(voucher)}
          >
            <FontAwesomeIcon icon={faLayerGroup} aria-hidden="true" />
            Split
          </button>
        </Tooltip>
      ) : null}
    </div>
  );

  const renderCard = (voucher) => {
    const meta = voucherStatus(voucher);
    return (
      <article
        key={voucher?._id}
        className="group rounded-gx border border-gx-border bg-gx-canvas/40 hover:bg-gx-surface hover:border-gx-navy/25 hover:shadow-gx transition overflow-hidden"
      >
        <div className="h-1 bg-gradient-to-r from-[#D71920] to-[#F7931E]" />
        <div className="p-4 space-y-4">
          <div className="flex items-start gap-3">
            <button type="button" className="text-left min-w-0 flex-1 cursor-pointer" onClick={() => openVoucher(voucher)}>
              <p className="font-semibold text-gx-ink truncate">{voucher?.invoice_no || "No number"}</p>
              <p className="text-xs text-gx-ink-soft truncate">{formatMaybeDate(voucher?.voucher_date)}</p>
              <div className="flex flex-wrap gap-1 mt-2">
                <StatusChip tone={meta.tone} label={meta.label} />
                {voucher?.voucher_type ? <StatusChip tone="navy" label={voucher.voucher_type} /> : null}
                {voucher?.parent_voucher_id && voucher?.installment_index && voucher?.total_installments ? (
                  <StatusChip
                    tone="warning"
                    label={`Installment ${voucher.installment_index}/${voucher.total_installments}`}
                  />
                ) : null}
                {voucher?.is_split ? <StatusChip tone="neutral" label="Split" /> : null}
              </div>
            </button>
            {renderActions(voucher)}
          </div>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-gx-ink-soft">Total</dt>
              <dd className="font-semibold tabular-nums">{formatMoney(voucherTotal(voucher))}</dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-gx-ink-soft">Paid</dt>
              <dd className="font-semibold tabular-nums">{formatMoney(voucher?.paid_amount || 0)}</dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-gx-ink-soft">Due</dt>
              <dd>{formatMaybeDate(voucherDueDate(voucher))}</dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-gx-ink-soft">Payment date</dt>
              <dd>{formatMaybeDate(voucherPaymentDate(voucher))}</dd>
            </div>
          </dl>
        </div>
      </article>
    );
  };

  return (
    <>
      <div className="relative m-5">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-8 left-1/2 h-56 w-[min(100%,920px)] -translate-x-1/2"
          style={{
            background:
              "radial-gradient(ellipse at 50% 0%, rgba(2, 42, 58, 0.07), transparent 70%)",
          }}
        />

        <PageHeader
          title="Vouchers"
          subtitle={
            memberName
              ? `Invoices and receipts for ${memberName}`
              : "Every invoice and receipt on this membership."
          }
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="gx-cta-secondary min-h-[44px] gap-2"
                onClick={handleNavigate}
              >
                <ArrowBackIcon fontSize="small" aria-hidden="true" />
                Back
              </button>
              <button
                type="button"
                className="gx-cta-secondary min-h-[44px] gap-2"
                onClick={() => navigate(`/transactions?subscription_id=${id}`)}
              >
                <ReceiptLongIcon fontSize="small" aria-hidden="true" />
                Transactions
              </button>
              {staffCanCreateVouchers(role) && allowVoucherCreation ? (
                <button type="button" className="gx-cta-secondary min-h-[44px] gap-2" onClick={handleAddVoucher}>
                  <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
                  Add voucher
                </button>
              ) : null}
              <button type="button" className="gx-cta min-h-[44px] gap-2" onClick={handleDownload}>
                <FontAwesomeIcon icon={faDownload} aria-hidden="true" />
                Export
              </button>
            </div>
          }
        />

        {loading ? (
          <div className="space-y-4" aria-busy="true" aria-live="polite">
            <span className="sr-only">Loading vouchers</span>
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={`kpi-${i}`} className="gx-glass rounded-gx px-5 py-5 min-h-[132px]">
                  <div className="h-3 w-24 rounded-gx bg-gx-border/70 animate-pulse" />
                  <div className="h-10 w-20 rounded-gx bg-gx-border/70 animate-pulse mt-6" />
                </div>
              ))}
            </div>
            <div className="gx-glass rounded-gx min-h-[140px] p-5">
              <div className="h-4 w-40 rounded-gx bg-gx-border/70 animate-pulse" />
              <div className="h-11 w-full rounded-gx bg-gx-border/70 animate-pulse mt-4" />
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
              {kpiCards.map((stat, index) => (
                <motion.div key={stat.key} {...stagger(index)}>
                  <StatCard
                    featured={stat.featured}
                    label={stat.label}
                    value={stat.value}
                    icon={stat.icon}
                    accent={stat.accent}
                    hint={stat.featured ? "Showing this roster" : stat.hint}
                    onClick={stat.key === "outstanding" ? undefined : () => selectStatus(stat.key)}
                  />
                </motion.div>
              ))}
            </div>

            <section className="gx-glass rounded-gx p-4 md:p-5">
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-sm font-semibold text-gx-ink">Find a voucher</h2>
                  <p className="text-xs text-gx-ink-soft mt-0.5">
                    Showing {filtered_vouchers.length.toLocaleString("en-US")} of{" "}
                    {(vouchers || []).length.toLocaleString("en-US")} vouchers
                  </p>
                </div>
                <div className="flex items-center gap-1 rounded-gx-control border border-gx-border bg-gx-surface p-1 self-start">
                  <button
                    type="button"
                    aria-pressed={layout === "cards"}
                    className={`min-h-[44px] min-w-[44px] rounded-gx-control px-3 text-sm font-semibold cursor-pointer transition ${
                      layout === "cards" ? "bg-gx-navy text-white" : "text-gx-navy hover:bg-gx-canvas"
                    }`}
                    onClick={() => setLayout("cards")}
                  >
                    <ViewModuleIcon fontSize="small" aria-hidden="true" />
                    <span className="sr-only">Card layout</span>
                  </button>
                  <button
                    type="button"
                    aria-pressed={layout === "table"}
                    className={`min-h-[44px] min-w-[44px] rounded-gx-control px-3 text-sm font-semibold cursor-pointer transition hidden md:inline-flex items-center justify-center ${
                      layout === "table" ? "bg-gx-navy text-white" : "text-gx-navy hover:bg-gx-canvas"
                    }`}
                    onClick={() => setLayout("table")}
                  >
                    <ViewListIcon fontSize="small" aria-hidden="true" />
                    <span className="sr-only">Table layout</span>
                  </button>
                </div>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleFilter();
                }}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                  <label className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft xl:col-span-2">
                    Search
                    <div className="relative mt-1.5">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gx-ink-soft"
                        aria-hidden="true"
                      />
                      <input
                        type="search"
                        placeholder="Voucher no, type, or member"
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        className={`${controlClass} pl-10`}
                      />
                    </div>
                  </label>
                  <label className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft">
                    Status
                    <select
                      value={status}
                      onChange={(e) => {
                        setStatus(e.target.value);
                        setPage(0);
                      }}
                      className={`${controlClass} mt-1.5`}
                    >
                      <option value="">All statuses</option>
                      <option value="Paid">Paid</option>
                      <option value="Pending">Pending</option>
                      <option value="overdue">Overdue</option>
                    </select>
                  </label>
                  <label className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft">
                    Type
                    <select
                      value={VoucherType}
                      onChange={(e) => {
                        setSubscriptionType(e.target.value);
                        setPage(0);
                      }}
                      className={`${controlClass} mt-1.5`}
                    >
                      <option value="">All types</option>
                      <option value="invoice">Invoice</option>
                      <option value="receipt">Receipt</option>
                    </select>
                  </label>
                  <label className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft">
                    Voucher from
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => {
                        setFromDate(e.target.value);
                        setPage(0);
                      }}
                      className={`${controlClass} mt-1.5`}
                    />
                  </label>
                  <label className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft">
                    Voucher to
                    <input
                      type="date"
                      value={toDate}
                      onChange={(e) => {
                        setToDate(e.target.value);
                        setPage(0);
                      }}
                      disabled={!fromDate}
                      min={fromDate}
                      className={`${controlClass} mt-1.5 ${!fromDate ? "bg-gx-canvas cursor-not-allowed text-gx-ink-soft" : ""}`}
                    />
                  </label>
                  <label className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft">
                    Payment from
                    <input
                      type="date"
                      value={paymentFromDate}
                      onChange={(e) => {
                        setPaymentFromDate(e.target.value);
                        setPage(0);
                      }}
                      className={`${controlClass} mt-1.5`}
                    />
                  </label>
                  <label className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft">
                    Payment to
                    <input
                      type="date"
                      value={paymentToDate}
                      onChange={(e) => {
                        setPaymentToDate(e.target.value);
                        setPage(0);
                      }}
                      disabled={!paymentFromDate}
                      min={paymentFromDate}
                      className={`${controlClass} mt-1.5 ${!paymentFromDate ? "bg-gx-canvas cursor-not-allowed text-gx-ink-soft" : ""}`}
                    />
                  </label>
                  <label className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft">
                    Due from
                    <input
                      type="date"
                      value={dueFromDate}
                      onChange={(e) => {
                        setDueFromDate(e.target.value);
                        setPage(0);
                      }}
                      className={`${controlClass} mt-1.5`}
                    />
                  </label>
                  <label className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft">
                    Due to
                    <input
                      type="date"
                      value={dueToDate}
                      onChange={(e) => {
                        setDueToDate(e.target.value);
                        setPage(0);
                      }}
                      disabled={!dueFromDate}
                      min={dueFromDate}
                      className={`${controlClass} mt-1.5 ${!dueFromDate ? "bg-gx-canvas cursor-not-allowed text-gx-ink-soft" : ""}`}
                    />
                  </label>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="submit" className="gx-cta min-h-[44px]">
                    Apply filters
                  </button>
                  <button type="button" className="gx-cta-secondary min-h-[44px]" onClick={handleClear}>
                    Clear
                  </button>
                </div>
              </form>
            </section>

            {filtered_vouchers.length === 0 ? (
              <div className="bg-gx-surface rounded-gx border border-gx-border shadow-gx">
                <EmptyState
                  title={filtersActive ? "No matching vouchers" : "No vouchers yet"}
                  description={
                    filtersActive
                      ? "Try a different number, date range, or clear the current filters."
                      : "Invoices and receipts will appear here once they are created for this membership."
                  }
                  action={
                    filtersActive ? (
                      <button type="button" className="gx-cta-secondary min-h-[44px]" onClick={handleClear}>
                        Clear filters
                      </button>
                    ) : null
                  }
                />
              </div>
            ) : (
              <div className="space-y-4">
                {layout === "table" ? (
                  <>
                    <TableShell className="hidden md:block">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Voucher</TableCell>
                            <TableCell>Dates</TableCell>
                            <TableCell>Total</TableCell>
                            <TableCell>Paid</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedVouchers.map((voucher) => {
                            if (voucher?.voucher_type !== "Invoice" && voucher?.voucher_type !== "Receipt") return null;
                            const meta = voucherStatus(voucher);
                            return (
                              <TableRow hover key={voucher?._id}>
                                <TableCell>
                                  <p className="font-semibold text-gx-ink">{voucher?.invoice_no || "—"}</p>
                                  {voucher?.parent_voucher_id && voucher?.installment_index && voucher?.total_installments ? (
                                    <p className="text-xs text-gx-ink-soft">
                                      Installment {voucher.installment_index}/{voucher.total_installments}
                                    </p>
                                  ) : null}
                                  {voucher?.is_split ? <p className="text-xs text-gx-ink-soft">Split parent</p> : null}
                                </TableCell>
                                <TableCell>
                                  <p className="text-sm">{formatMaybeDate(voucher?.voucher_date)}</p>
                                  <p className="text-xs text-gx-ink-soft">Due {formatMaybeDate(voucherDueDate(voucher))}</p>
                                  <p className="text-xs text-gx-ink-soft">Paid {formatMaybeDate(voucherPaymentDate(voucher))}</p>
                                </TableCell>
                                <TableCell className="font-semibold tabular-nums">{formatMoney(voucherTotal(voucher))}</TableCell>
                                <TableCell className="tabular-nums">{formatMoney(voucher?.paid_amount || 0)}</TableCell>
                                <TableCell>
                                  {voucher?.voucher_type ? <StatusChip tone="navy" label={voucher.voucher_type} /> : "—"}
                                </TableCell>
                                <TableCell>
                                  <StatusChip tone={meta.tone} label={meta.label} />
                                </TableCell>
                                <TableCell align="right">{renderActions(voucher)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableShell>
                    <div className="md:hidden grid grid-cols-1 gap-4">
                      {paginatedVouchers.map(renderCard)}
                    </div>
                  </>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {paginatedVouchers.map(renderCard)}
                  </div>
                )}
                <div className="bg-gx-surface rounded-gx border border-gx-border shadow-gx overflow-x-auto">
                  <Table>
                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                          colSpan={1}
                          count={filtered_vouchers.length}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          slotProps={{
                            select: {
                              inputProps: { "aria-label": "rows per page" },
                              native: true,
                            },
                          }}
                          onPageChange={handleChangePage}
                          onRowsPerPageChange={handleChangeRowsPerPage}
                          ActionsComponent={TablePaginationActions}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <CustomModal
        isModal={isSplitModalOpen}
        handleModal={() => {
          setIsSplitModalOpen(false);
          setSplitSubscriptionResolved(null);
          setSplitDateError('');
          setSplitInstallmentDueDates([]);
          setSplitAmountMode('equal');
          setSplitCustomAmounts([]);
          setSplitAmountError('');
        }}
        size={'xl'}
      >
        <h2 className="m-5 text-center text-xl font-medium text-gx-ink-soft">Split Voucher into Installments</h2>
        <div className='mx-10 p-4 border border-gx-border rounded-lg'>
          <div className='flex flex-col gap-3 text-gx-ink-soft'>
            <div className='flex justify-between items-center bg-gx-canvas p-2 rounded'>
              <span className='font-bold text-sm'>Voucher No:</span>
              <span className='font-semibold'>{splitTargetVoucher?.invoice_no}</span>
            </div>
            <div className='flex justify-between items-center bg-gx-canvas p-2 rounded'>
              <span className='font-bold text-sm'>Invoice total (fees):</span>
              <span className='font-semibold'>{splitTargetVoucher?.invoice?.total_amount}</span>
            </div>
            <div className='flex justify-between items-center bg-gx-canvas p-2 rounded border border-gx-border'>
              <span className='font-bold text-sm'>Outstanding to split (pending):</span>
              <span className='font-semibold'>{splitTargetVoucher?.invoice?.pending_amount}</span>
            </div>
            <div className='flex justify-between items-center bg-gx-canvas p-2 rounded'>
              <span className='font-bold text-sm'>Subscription period:</span>
              <span className='font-semibold'>
                {(() => {
                  const { start, end } = getSplitSubscriptionPeriod();
                  if (!start || !end) return 'Missing — loading or unavailable';
                  return `${start.format('MMM DD, YYYY')} → ${end.format('MMM DD, YYYY')}`;
                })()}
              </span>
            </div>

            <div className='border-t border-gx-border my-2'></div>

            <div className='flex flex-col gap-2'>
              <label className='font-bold'>Number of Installments (min 2):</label>
              <input
                type='number'
                min={2}
                step={1}
                className='font-semibold border border-blue-400 w-full p-2 rounded-lg outline-0 focus:ring-2 focus:ring-blue-200'
                value={splitNumberOfInstallments}
                onChange={(e) => setSplitNumberOfInstallments(e.target.value)}
              />
              <label className='font-bold mt-2'>Amount split mode:</label>
              <div className='flex flex-wrap gap-4'>
                <label className='flex items-center gap-2 cursor-pointer'>
                  <input
                    type='radio'
                    name='splitAmountMode'
                    checked={splitAmountMode === 'equal'}
                    onChange={() => {
                      setSplitAmountMode('equal');
                      setSplitAmountError('');
                    }}
                  />
                  <span className='text-sm'>Divide equally</span>
                </label>
                <label className='flex items-center gap-2 cursor-pointer'>
                  <input
                    type='radio'
                    name='splitAmountMode'
                    checked={splitAmountMode === 'custom'}
                    onChange={() => setSplitAmountMode('custom')}
                  />
                  <span className='text-sm'>Custom amounts</span>
                </label>
              </div>
              <p className='text-xs text-gx-ink-soft italic'>
                {splitAmountMode === 'equal'
                  ? '* Equal mode: the last installment absorbs any rounding so the total stays exact.'
                  : '* Custom mode: enter each installment amount; the sum must equal the outstanding pending amount.'}
              </p>
              <p className='text-xs text-gx-ink-soft italic'>
                * Due dates auto-fill across the subscription period; override any row to customize.
              </p>
            </div>

            <div className='mt-2'>
              <h3 className='font-bold mb-2'>Preview:</h3>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <table className='w-full border border-gx-border rounded'>
                  <thead className='bg-gx-canvas'>
                    <tr>
                      <th className='text-left p-2 text-sm'>Installment</th>
                      <th className='text-left p-2 text-sm'>Amount</th>
                      <th className='text-left p-2 text-sm'>Due Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSplitDisplayAmounts().map((share, i, arr) => {
                      const { end } = getSplitSubscriptionPeriod();
                      const minD = dayjs().startOf('day');
                      const maxD =
                        end && end.isValid() && !end.endOf('day').isBefore(minD, 'day')
                          ? end.endOf('day')
                          : undefined;
                      const raw = splitInstallmentDueDates[i];
                      const value = raw != null ? normalizeSplitDate(raw) : null;
                      const principal = splitPreviewPrincipal(splitTargetVoucher);
                      return (
                        <tr key={i} className='border-t border-gx-border'>
                          <td className='p-2 text-sm'>{i + 1} of {arr.length}</td>
                          <td className='p-2 text-sm'>
                            {splitAmountMode === 'custom' ? (
                              <input
                                type='number'
                                min={1}
                                step={1}
                                className='w-full border border-blue-300 rounded p-1 text-sm'
                                value={splitCustomAmounts[i] ?? ''}
                                onChange={(e) => {
                                  const nInst = Math.max(
                                    2,
                                    Math.floor(Number(splitNumberOfInstallments) || 0)
                                  );
                                  const next = Array.from(
                                    { length: nInst },
                                    (_, j) =>
                                      j < splitCustomAmounts.length
                                        ? splitCustomAmounts[j]
                                        : ''
                                  );
                                  next[i] = e.target.value;
                                  setSplitCustomAmounts(next);
                                  setSplitAmountError(
                                    validateInstallmentAmounts(next, principal, nInst)
                                  );
                                }}
                              />
                            ) : (
                              share
                            )}
                          </td>
                          <td className='p-2 text-sm align-top'>
                            <div className='min-w-[220px]'>
                              <DatePicker
                                value={value}
                                minDate={minD}
                                maxDate={maxD}
                                referenceDate={minD}
                                onChange={(d) => {
                                  const nInst = Math.max(
                                    2,
                                    Math.floor(Number(splitNumberOfInstallments) || 0)
                                  );
                                  const next = Array.from(
                                    { length: nInst },
                                    (_, j) =>
                                      j < splitInstallmentDueDates.length
                                        ? splitInstallmentDueDates[j]
                                        : null
                                  );
                                  next[i] = d ? normalizeSplitDate(d) : null;
                                  setSplitInstallmentDueDates(next);
                                  const { end: e } = getSplitSubscriptionPeriod();
                                  setSplitDateError(validateSplitDueDates(next, e));
                                }}
                                slotProps={{
                                  textField: {
                                    size: 'small',
                                    fullWidth: true,
                                  },
                                }}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </LocalizationProvider>
              {splitAmountMode === 'custom' && (
                <p className='text-xs text-gx-ink-soft mt-2'>
                  Total:{' '}
                  <span className='font-semibold'>
                    {getSplitDisplayAmounts().reduce((s, a) => s + a, 0)}
                  </span>{' '}
                  / {splitPreviewPrincipal(splitTargetVoucher)} (outstanding)
                </p>
              )}
              {splitDateError && (
                <p className='text-xs text-red-600 mt-2 font-semibold'>{splitDateError}</p>
              )}
              {splitAmountError && (
                <p className='text-xs text-red-600 mt-2 font-semibold'>{splitAmountError}</p>
              )}
            </div>
          </div>
        </div>

        <div className='m-5 text-center'>
          <div className='flex justify-center items-center gap-3 my-5'>
            <button
              className={`gx-cta min-h-[44px] px-8 ${splitButtonLoading || splitDateError || splitAmountError ? 'cursor-not-allowed opacity-60' : ''}`}
              onClick={handleSplitSubmit}
              disabled={splitButtonLoading || !!splitDateError || !!splitAmountError}
            >
              Split
              {splitButtonLoading && (
                <span className='ml-2 inline-block align-middle'><CircularProgress size={18} color="inherit" /></span>
              )}
            </button>
            <button
              className='gx-cta-secondary min-h-[44px] px-8'
              onClick={() => {
                setIsSplitModalOpen(false);
                setSplitSubscriptionResolved(null);
                setSplitDateError('');
                setSplitInstallmentDueDates([]);
                setSplitAmountMode('equal');
                setSplitCustomAmounts([]);
                setSplitAmountError('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </CustomModal>

      {popup.open && (
        <Popup
          alertType={popup.type === 'success' ? 'success' : 'failure'}
          message={popup.message}
          onClose={() => setPopup({ ...popup, open: false })}
        />
      )}
    </>
  )
}

export default VouchersTable